import { queryOptions, useQuery } from "@tanstack/react-query";
import { supabaseClient } from "@/lib/supabase";
import { useAuth } from "@/utils/auth";
import type { Database } from "@/types/database/database";
import type { EventTypeValue, GrantKey } from "../types";
import { ACCESS_ATTRIBUTES, EVENT_ATTRIBUTES, type AccessAttribute } from "../constants";

type AccessModeRow = Database["public"]["Tables"]["user_access_modes"]["Row"];

export type AccessModeFlags = {
    enabled: boolean;
    public_access: boolean;
    can_add: boolean;
};

export type MyAccessModes = Record<AccessAttribute, AccessModeFlags>;

function disabledMode(): AccessModeFlags {
    return { enabled: false, public_access: false, can_add: false };
}

export function emptyMyAccessModes(): MyAccessModes {
    return Object.fromEntries(ACCESS_ATTRIBUTES.map((attribute) => [attribute, disabledMode()])) as MyAccessModes;
}

export async function fetchMyAccessModes(userId: string): Promise<MyAccessModes> {
    const modes = emptyMyAccessModes();
    if (!userId) return modes;

    const { data, error } = await supabaseClient
        .from("user_access_modes")
        .select("attribute, enabled, public_access, can_add")
        .eq("user_id", userId);

    if (error) throw error;

    for (const row of (data ?? []) as Pick<AccessModeRow, "attribute" | "enabled" | "public_access" | "can_add">[]) {
        modes[row.attribute] = {
            enabled: row.enabled,
            public_access: row.public_access,
            can_add: row.can_add,
        };
    }

    return modes;
}

export const myAccessModesQueryKey = (userId: string) => ["clinical-access", "my-modes", userId] as const;

export const myAccessModesQueryOptions = (userId: string) =>
    queryOptions({
        queryKey: myAccessModesQueryKey(userId),
        queryFn: () => fetchMyAccessModes(userId),
        enabled: Boolean(userId),
        staleTime: 30_000,
    });

export function useMyAccessModes() {
    const auth = useAuth();
    return useQuery(myAccessModesQueryOptions(auth?.id ?? ""));
}

/** Event types that appear on the template intervention step when enabled. Fail-closed until modes load. */
export function enabledEventTypes(modes: MyAccessModes | undefined): EventTypeValue[] {
    if (!modes) return [];
    return EVENT_ATTRIBUTES.filter((attribute) => modes[attribute]?.enabled);
}

export function canAddAttribute(modes: MyAccessModes | undefined, attribute: AccessAttribute | GrantKey): boolean {
    if (!modes) return false;
    const mode = modes[attribute as AccessAttribute];
    return Boolean(mode?.enabled && mode?.can_add);
}
